import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, CheckCircle2, TrendingUp, Clock, Zap } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Python', icon: FileText },
  { name: 'Microsoft Dynamics CRM', icon: Database },
  { name: 'Document Processing', icon: FileText },
  { name: 'Automated Workflows', icon: Cloud }
];

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '95%',
    label: 'Reduction in Manual Entry',
    description: 'Through intelligent parsing and automated field extraction'
  },
  {
    icon: Zap,
    value: '500+',
    label: 'Daily Statements Automated',
    description: 'From multiple insurance carriers with zero manual intervention'
  },
  {
    icon: Clock,
    value: '15 min',
    label: 'Processing Time',
    description: 'Reduced from 4 hours to 15 minutes per batch'
  },
  {
    icon: CheckCircle2,
    value: '99.8%',
    label: 'Data Accuracy',
    description: 'Through Python-based validation rules'
  }
];

const highlights = [
  {
    category: 'Automation Excellence',
    items: [
      'Automated processing of 500+ daily statements from multiple insurance carriers with zero manual intervention required',
      'Achieved 95% reduction in manual data entry through intelligent parsing and automated field extraction',
      'Reduced processing time from 4 hours to 15 minutes per batch through automation'
    ]
  },
  {
    category: 'Integration & Accuracy',
    items: [
      'Enabled real-time CRM synchronization ensuring policy data accuracy and immediate availability',
      'Enhanced compliance reporting with automated audit trails and regulatory documentation',
      'Maintained 99.8% data accuracy through Python-based validation rules'
    ]
  }
];

const challenges = [
  {
    title: 'Multi-Format Document Chaos',
    icon: FileText,
    problem: 'Insurance carriers sent statements in various formats some as PDFs with different layouts, others as Excel spreadsheets with inconsistent column structures, and many as text attachments with unstructured data making standardized processing nearly impossible.',
    solution: 'Our automation specialists developed a comprehensive Python-based parsing engine capable of intelligently identifying document types and extracting relevant data regardless of format. The system uses advanced OCR for PDF processing, structured data extraction for Excel files, and natural language processing for text-based statements.',
    impact: 'Universal document format processing capability'
  },
  {
    title: 'High-Volume Daily Processing',
    icon: Zap,
    problem: 'With 500+ statements arriving daily from dozens of insurance carriers, manual processing created significant bottlenecks, delays in policy updates, and increased risk of data entry errors affecting client servicing and billing accuracy.',
    solution: 'We implemented an automated email processing system with on-premise listeners that continuously monitor incoming statements, extract attachments, and queue documents for batch processing. The system processes statements in real-time as they arrive, eliminating delays and ensuring immediate data availability.',
    impact: 'Real-time processing of 500+ daily statements'
  },
  {
    title: 'Data Validation Complexity',
    icon: Database,
    problem: 'Insurance statements required complex validation against existing policies, carrier-specific business rules, and regulatory compliance requirements, making automated processing challenging without human oversight.',
    solution: 'Using Python, we built a sophisticated business rules engine that automatically validates statement data against existing CRM records, applies carrier-specific processing rules, and generates automated alerts for exceptions. The system maintains comprehensive audit logs for compliance and error tracking.',
    impact: 'Automated validation with 99.8% accuracy'
  },
  {
    title: 'CRM Integration Requirements',
    icon: Cloud,
    problem: 'The brokerage\'s Microsoft Dynamics CRM contained years of policy data and existing workflows that could not be disrupted, requiring any automation solution to integrate seamlessly with established business processes.',
    solution: 'We developed custom Dynamics API connectors that automatically upload processed statement data directly into the appropriate CRM records. The integration includes error handling, duplicate detection, and automated reporting hooks that notify staff of successful uploads or processing exceptions.',
    impact: 'Seamless CRM integration with zero workflow disruption'
  }
];

const InsuranceAutomation = () => {
  return (
    <>
      <SEOHead
        title="Insurance Statement Automation Case Study | Agility AI"
        description="How Agility automated 500+ daily insurance statements for a UK brokerage cutting processing time from 4 hours to 15 minutes and achieving 95% reduction in manual data entry via Python and Microsoft Dynamics CRM integration."
        url="https://www.agilitytech.ai/case-studies/insurance-automation"
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Insurance Statement Automation: From 4 Hours to 15 Minutes",
          "description": "How Agility automated 500+ daily insurance statements for a UK brokerage cutting processing time from 4 hours to 15 minutes and achieving 95% reduction in manual data entry.",
          "author": { "@type": "Organization", "name": "Agility" },
          "publisher": {
            "@type": "Organization",
            "name": "Agility",
            "logo": { "@type": "ImageObject", "url": "https://www.agilitytech.ai/agility hex fonts.png" }
          },
          "datePublished": "2025-10-01",
          "dateModified": "2026-05-01",
          "mainEntityOfPage": { "@type": "WebPage", "@id": "https://www.agilitytech.ai/case-studies/insurance-automation" }
        }}
      />
    <div className="pt-20 bg-black">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80"
            alt="Insurance Automation Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
              <span className="text-teal-400 text-sm font-medium">Financial Services/Insurance</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Automated Statement Processing Platform
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              Transforming insurance operations through intelligent document processing and seamless 
              CRM integration for a leading brokerage firm.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <metric.icon className="h-6 w-6 text-teal-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                <p className="text-gray-400 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
              <p className="text-white">Financial Services/Insurance</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
              <p className="text-white">North America</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
              <p className="text-white">Enterprise Implementation</p>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
              <p className="text-white">Q1 2024 - Completed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                <tech.icon className="h-6 w-6 text-teal-400" />
                <span className="text-gray-300 font-medium">{tech.name}</span>
              </div>
            ))}
            <div className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
              <Shield className="h-6 w-6 text-teal-400" />
              <span className="text-gray-300 font-medium">Enterprise Security</span>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <FileText className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Multi-Format Document Processing Crisis</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    A leading insurance brokerage firm managing policies across multiple carriers was 
                    overwhelmed by hundreds of statements arriving daily from various insurers. Each 
                    statement came in different formats PDFs, Excel spreadsheets, or text attachments requiring 
                    manual consolidation, normalization, and data entry into Microsoft Dynamics CRM for 
                    policy management, billing reconciliation, and regulatory reporting.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
              <div className="flex items-start space-x-4 mb-8">
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Intelligent Document Processing Revolution</h3>
                  <p className="text-xl text-gray-300 leading-relaxed">
                    We replaced labor-intensive, error-prone manual processing with an intelligent, 
                    automated system that transformed statement handling across the entire brokerage 
                    operation. By implementing sophisticated document processing algorithms with seamless 
                    CRM integration, we delivered operational excellence that exceeded all performance 
                    expectations while maintaining full regulatory compliance.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {highlights.map((category, index) => (
                <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                  <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                  <div className="space-y-4">
                    {category.items.map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                        <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {challenges.map((challenge, index) => (
              <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 
                border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                    <challenge.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                      Problem
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                      Solution
                    </h4>
                    <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                  </div>
                  
                  <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                    <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                    <p className="text-teal-300 font-medium">{challenge.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="h-8 w-8 text-teal-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Eliminate Manual Business Tasks?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Contact our business automation specialists to discover how document processing automation 
              can transform your business operations.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Get Started Today</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default InsuranceAutomation;